import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  History,
  Link2,
  Pencil,
  RotateCcw,
  Ticket as IconeDeChamado,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { formatarDistancia } from '@/lib/format';
import { usePode } from '@/features/knowledge/permissoes';
import { LinkDeChamado } from '@/features/tickets/navigation';
import { AvisoDeFalha } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { Campo, CLASSE_DE_CAMPO } from '@/features/changes/componentes';
import { Modal } from '@/features/tickets/Modal';
import {
  chavesDeConhecimento,
  obterArtigo,
  obterCicloDeVida,
  restaurarVersao,
  transicionarArtigo,
} from '@/features/knowledge/conhecimento.api';
import {
  CorpoDoArtigo,
  EtiquetaDeRevisao,
  EtiquetaDeSituacao,
  EtiquetaDeVisibilidade,
  Utilidade,
} from '@/features/knowledge/componentes';
import {
  CAMINHO_ARTIGO_EDITAR,
  LinkDaLista,
  useIrPara,
} from '@/features/knowledge/navegacao';
import {
  ALCANCE_DE_VISIBILIDADE,
  EFEITO_DE_SITUACAO,
  revisaoVencida,
  ROTULO_DE_SITUACAO,
  type Artigo,
  type SituacaoDeArtigo,
  type Transicao,
} from '@/features/knowledge/tipos';

/**
 * A ficha do artigo.
 *
 * Três coisas que a tela precisa dizer e que uma ficha comum não diria:
 *
 *   1. **quem alcança este texto** — a etiqueta de público fica ao lado do
 *      título, não escondida numa aba de propriedades. Publicar para "Cliente"
 *      um procedimento com senha de firewall é o erro mais caro desta fase;
 *   2. **se o conteúdo ainda vale** — revisão vencida vira aviso no topo, para
 *      quem lê, e não só um filtro para quem administra;
 *   3. **que botões existem daqui** — o grafo do ciclo de vida vem do servidor
 *      (`GET /knowledge/articles/lifecycle`), e a tela desenha o que ele diz.
 *      Duplicar a máquina de estados aqui garantiria que as duas divergissem.
 */
export function ArtigoPage({ artigoId }: { artigoId: string }) {
  const cliente = useQueryClient();
  const navegar = useIrPara();
  const [falha, setFalha] = useState<Falha | null>(null);
  const [transicaoAberta, setTransicaoAberta] = useState<Transicao | null>(null);

  const consulta = useQuery({
    queryKey: chavesDeConhecimento.artigo(artigoId),
    queryFn: () => obterArtigo(artigoId),
  });
  const ciclo = useQuery({
    queryKey: chavesDeConhecimento.ciclo,
    queryFn: obterCicloDeVida,
    // O grafo não muda entre requisições: é código, não dado.
    staleTime: Infinity,
  });

  /**
   * Só as transições que esta pessoa pode de fato executar.
   *
   * Filtrar apenas por situação mostrava "Aprovar" e "Publicar" a um técnico
   * com `knowledge.create` e mais nada — ele clicava e recebia 403. O payload
   * de `/lifecycle` já traz a permissão de cada transição; usá-la é uma linha,
   * e é a diferença entre uma tela que ensina o processo e uma que engana.
   *
   * Isto é conveniência, não segurança: quem decide continua sendo o serviço.
   *
   * ⚠ Fica AQUI, junto dos outros hooks, e não lá embaixo perto de onde é
   * usado: abaixo há dois `return` antecipados (carregando e erro), e um hook
   * depois deles é chamado condicionalmente — o React quebra com "rendered
   * fewer hooks than expected" no instante em que a consulta sai de pendente.
   */
  const pode = usePode();

  const invalidar = () => {
    void cliente.invalidateQueries({ queryKey: chavesDeConhecimento.artigo(artigoId) });
    void cliente.invalidateQueries({ queryKey: chavesDeConhecimento.versoes(artigoId) });
    void cliente.invalidateQueries({ queryKey: chavesDeConhecimento.raiz });
  };

  const transicionar = useMutation({
    mutationFn: (entrada: { to: SituacaoDeArtigo; note?: string; version: number }) =>
      transicionarArtigo(artigoId, entrada),
    onSuccess: () => {
      setFalha(null);
      setTransicaoAberta(null);
      invalidar();
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const restaurar = useMutation({
    mutationFn: (numero: number) => restaurarVersao(artigoId, { number: numero }),
    onSuccess: () => {
      setFalha(null);
      invalidar();
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  if (consulta.isPending) {
    return (
      <SkeletonRegion label="Carregando artigo" className="mx-auto w-full max-w-4xl px-4 py-6">
        <Skeleton className="mb-3 h-6 w-2/3" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </SkeletonRegion>
    );
  }
  if (consulta.isError) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-6">
        <AvisoDeFalha falha={classificarFalha(consulta.error)} aoRecarregar={() => void consulta.refetch()} />
      </div>
    );
  }

  const artigo: Artigo = consulta.data;
  const possiveis = (ciclo.data?.transitions ?? [])
    .filter((t) => t.de === artigo.status)
    .filter((t) => pode(t.permissao));
  const vencido = revisaoVencida(artigo.nextReviewAt);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-6 sm:px-6">
      <LinkDaLista className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Base de conhecimento
      </LinkDaLista>

      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-muted-foreground">{artigo.code}</span>
          <EtiquetaDeSituacao situacao={artigo.status} />
          <EtiquetaDeVisibilidade visibilidade={artigo.visibility} />
          <EtiquetaDeRevisao nextReviewAt={artigo.nextReviewAt} />
          <span className="text-xs text-muted-foreground">versão {artigo.currentVersion}</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{artigo.title}</h1>
        <p className="text-sm text-muted-foreground">{artigo.summary}</p>
        <div className="flex flex-wrap items-center gap-3">
          <Utilidade
            resolvidos={artigo.resolvedCount}
            usos={artigo.usageCount}
            uteis={artigo.helpfulCount}
            inuteis={artigo.notHelpfulCount}
          />
        </div>
      </header>

      {/*
        O aviso é para QUEM LÊ, e não para quem administra. Um artigo vencido
        continua no ar de propósito — tirá-lo puniria o leitor pelo atraso do
        dono —, e por isso a tela tem de dizer que ele pode estar desatualizado.
      */}
      {vencido && (
        <div
          role="status"
          className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning"
        >
          A data de revisão deste artigo passou. Ele continua disponível, mas o conteúdo pode
          estar desatualizado — confirme antes de seguir os passos.
        </div>
      )}

      {falha && <AvisoDeFalha falha={falha} />}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          onClick={() => navegar({ to: CAMINHO_ARTIGO_EDITAR, params: { artigoId } })}
        >
          <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
          Editar
        </Button>
        {possiveis.map((t) => (
          <Button
            key={t.para}
            variant={t.para === 'PUBLISHED' ? 'primary' : 'secondary'}
            onClick={() => setTransicaoAberta(t)}
            title={t.explicacao}
          >
            {t.rotulo}
          </Button>
        ))}
      </div>

      <Card>
        <CardBody>
          <CorpoDoArtigo html={artigo.bodyHtml} />
        </CardBody>
      </Card>

      {artigo.keywords.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">Palavras-chave</h2>
          <p className="text-xs text-muted-foreground">
            Sinônimos e siglas que o texto não usa. Entram na busca com o peso do título — são
            justamente as palavras que quem procura digita e o autor não escreveu.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {artigo.keywords.map((k) => (
              <span
                key={k}
                className="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground ring-1 ring-inset ring-border"
              >
                {k}
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Link2 className="h-4 w-4" aria-hidden />
          Vínculos
        </h2>
        <dl className="space-y-2 text-sm">
          <div className="flex flex-wrap items-baseline gap-2">
            <dt className="text-muted-foreground">Nasceu do chamado:</dt>
            <dd>
              {artigo.sourceTicket ? (
                <LinkDeChamado
                  ticketId={artigo.sourceTicket.id}
                  className="text-primary hover:underline"
                >
                  {artigo.sourceTicket.number} — {artigo.sourceTicket.subject}
                </LinkDeChamado>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </dd>
          </div>
          {artigo.knownErrors.length > 0 && (
            <div className="flex flex-wrap items-baseline gap-2">
              <dt className="text-muted-foreground">Publica o erro conhecido:</dt>
              <dd className="font-mono text-xs">
                {artigo.knownErrors.map((k) => `${k.code} — ${k.title}`).join('; ')}
              </dd>
            </div>
          )}
        </dl>

        {artigo.ticketLinks.length > 0 && (
          <div className="space-y-1.5 pt-2">
            <h3 className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              <IconeDeChamado className="h-3.5 w-3.5" aria-hidden />
              Chamados que usaram este artigo
            </h3>
            <ul className="space-y-1">
              {artigo.ticketLinks.map((l) => (
                <li key={l.ticketId} className="flex flex-wrap items-baseline gap-2 text-sm">
                  <LinkDeChamado ticketId={l.ticketId} className="text-primary hover:underline">
                    {l.ticket.number}
                  </LinkDeChamado>
                  <span className="text-muted-foreground">{l.ticket.subject}</span>
                  {l.resolved && (
                    <span className="text-xs font-semibold text-success">resolveu</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {formatarDistancia(l.usedAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <HistoricoDeVersoes
        artigo={artigo}
        aoRestaurar={(numero) => restaurar.mutate(numero)}
        restaurando={restaurar.isPending}
      />

      {transicaoAberta && (
        <DialogoDeTransicao
          artigo={artigo}
          transicao={transicaoAberta}
          enviando={transicionar.isPending}
          aoFechar={() => setTransicaoAberta(null)}
          aoConfirmar={(nota) =>
            transicionar.mutate({
              to: transicaoAberta.para,
              ...(nota.trim() !== '' ? { note: nota.trim() } : {}),
              version: artigo.version,
            })
          }
        />
      )}
    </div>
  );
}

/**
 * O histórico — critério 8.2.
 *
 * Restaurar cria a PRÓXIMA versão, e não reescreve a corrente. O texto do botão
 * diz isso, porque a expectativa natural de quem clica em "restaurar" é a
 * oposta, e descobrir depois que a versão 7 continua no histórico assusta.
 */
function HistoricoDeVersoes({
  artigo,
  aoRestaurar,
  restaurando,
}: {
  artigo: Artigo;
  aoRestaurar: (numero: number) => void;
  restaurando: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const versoes = artigo.versions;

  return (
    <section className="space-y-2">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-primary"
        aria-expanded={aberto}
      >
        <History className="h-4 w-4" aria-hidden />
        Histórico ({versoes.length} versão{versoes.length === 1 ? '' : 'ões'})
      </button>

      {aberto && (
        <ul className="space-y-2">
          {versoes.map((v) => (
            <li key={v.id}>
              <Card>
                <CardBody className="flex flex-wrap items-start justify-between gap-3 py-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-mono font-semibold text-foreground">v{v.number}</span>
                      {v.number === artigo.currentVersion && (
                        <span className="text-primary">corrente</span>
                      )}
                      <span className="text-muted-foreground">
                        {ROTULO_DE_SITUACAO[v.status]} · {formatarDistancia(v.createdAt)}
                      </span>
                      {v.restoredFrom !== null && (
                        <span className="text-muted-foreground">
                          restaurada da v{v.restoredFrom}
                        </span>
                      )}
                    </div>
                    <p className="truncate text-sm text-foreground">{v.title}</p>
                    {v.changeNote && (
                      <p className="text-xs text-muted-foreground">{v.changeNote}</p>
                    )}
                  </div>
                  {v.number !== artigo.currentVersion && (
                    <Button
                      variant="secondary"
                      disabled={restaurando}
                      onClick={() => aoRestaurar(v.number)}
                      title={`Cria a versão ${artigo.currentVersion + 1} com o texto da v${v.number}. Nada é apagado.`}
                    >
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      Restaurar
                    </Button>
                  )}
                </CardBody>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * A confirmação da transição.
 *
 * Mostra o que a situação de destino FAZ, e não só o nome dela: "Aprovado" é o
 * que mais se erra, porque quem lê a palavra supõe que o artigo foi ao ar.
 */
function DialogoDeTransicao({
  artigo,
  transicao,
  enviando,
  aoFechar,
  aoConfirmar,
}: {
  artigo: Artigo;
  transicao: Transicao;
  enviando: boolean;
  aoFechar: () => void;
  aoConfirmar: (nota: string) => void;
}) {
  const [nota, setNota] = useState('');
  const exigeMotivo = transicao.de === 'IN_REVIEW' && transicao.para === 'DRAFT';
  const publicando = transicao.para === 'PUBLISHED';

  return (
    <Modal aberto titulo={transicao.rotulo} aoFechar={aoFechar}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{transicao.explicacao}</p>
        <p className="rounded-md bg-secondary px-3 py-2 text-sm text-secondary-foreground">
          Depois desta ação: {EFEITO_DE_SITUACAO[transicao.para]}
        </p>

        {publicando && (
          <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
            Este artigo vai ao ar como <strong>{artigo.visibility === 'INTERNAL' ? 'interno' : artigo.visibility === 'CUSTOMER' ? 'cliente' : 'público'}</strong>:{' '}
            {ALCANCE_DE_VISIBILIDADE[artigo.visibility]} A revisão será agendada para daqui a{' '}
            {artigo.reviewIntervalDays} dias.
          </p>
        )}

        <Campo
          rotulo={exigeMotivo ? 'Motivo da devolução' : 'Nota (opcional)'}
          htmlFor="nota-transicao"
          dica={
            exigeMotivo
              ? 'Obrigatório: sem dizer o que falta, a próxima versão volta com o mesmo problema.'
              : 'Fica na trilha de auditoria.'
          }
        >
          <textarea
            id="nota-transicao"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            rows={3}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={aoFechar} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            onClick={() => aoConfirmar(nota)}
            disabled={enviando || (exigeMotivo && nota.trim() === '')}
          >
            {transicao.rotulo}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

